import React, { useState } from 'react';
import { useHistory } from 'react-router';
import {
    Box,
    Card,
    CardHeader,
    CardActions,
    Link,
    ListItemText,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme,
    styled
} from '@mui/material';
import FormSelectField from '../../common/components/FormSelectField';
import TertiaryButton from '../../common/components/TertiaryButton';
import usePlanSelection from './hooks/usePlanSelection';
import { COACH_LICENSE_TYPES } from '../constants';
import SecondaryButton from '../../common/components/SecondaryButton';
import { SubscriptionOptionsResponse, SubscriptionOption } from '../types';
import { navigate } from '../../state/navigation/navigation';

interface Props {
    subscriptionOptions: SubscriptionOptionsResponse;
    currentPlan: SubscriptionOption;
    selectedPlan: SubscriptionOption;
    setSelectedPlan: (plan: SubscriptionOption) => void;
    handlePaymentPreview: (plan: SubscriptionOption) => Promise<void>;
    handleDowngrade: () => Promise<void>;
}

const HelperTextBox = styled(Box)({
    padding: 12,
    marginBottom: 4
});

export default function PlanSelection({
    subscriptionOptions,
    currentPlan,
    selectedPlan,
    setSelectedPlan,
    handlePaymentPreview,
    handleDowngrade
}: Props) {
    const history = useHistory();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { menuOpen, setMenuOpen, isTrial, numDaysRemainingInTrial } = usePlanSelection();
    const [isUpgradeOrDowngradeButtonLoading, setIsUpgradeOrDowngradeButtonLoading] = useState(false);

    const { isMarketplaceCoach, isCancelPending, hasYearlyLicense, isLicenseOverdue, subscriptions, nextBillingDate } =
        subscriptionOptions;

    const { coachLicenseId, isUpgrade } = selectedPlan;

    const cancelChanges = () => {
        setSelectedPlan(currentPlan);
    };

    const generateButtonText = () => {
        let buttonText = 'Upgrade Plan';

        if (selectedPlan !== currentPlan) {
            buttonText = isUpgrade ? 'Upgrade' : 'Downgrade';
        }

        if (isLicenseOverdue) {
            buttonText = 'Go to Payment Info';
        }

        return buttonText;
    };

    const generatePlanOptions = () => {
        return (
            subscriptions?.length &&
            subscriptions.map((option) => {
                const selected = coachLicenseId === option.coachLicenseId ? 'select-form-selected' : '';
                const disabled = option.isEnabled ? '' : 'option-disabled';

                return (
                    // @ts-ignore
                    <MenuItem key={option.coachLicenseId} value={option} style={{ flexDirection: 'row' }}>
                        <ListItemText
                            primary={option.title}
                            classes={{
                                primary: `${disabled} ${selected}`
                            }}
                        />
                    </MenuItem>
                );
            })
        );
    };

    const handleButtonClick = async () => {
        if (isLicenseOverdue) {
            navigate('/payment');
        } else if (selectedPlan === currentPlan) {
            setMenuOpen(true);
        } else if (isUpgrade) {
            setIsUpgradeOrDowngradeButtonLoading(true);
            await handlePaymentPreview(selectedPlan);
        } else if (!isUpgrade) {
            setIsUpgradeOrDowngradeButtonLoading(true);
            await handleDowngrade();
        }
        setIsUpgradeOrDowngradeButtonLoading(false);
    };

    const isTrialOrBasic =
        currentPlan.coachLicenseId === COACH_LICENSE_TYPES.TRIAL ||
        currentPlan.coachLicenseId === COACH_LICENSE_TYPES.BASIC;

    const showCancelPlanButton = selectedPlan === currentPlan && !isTrialOrBasic && !isLicenseOverdue;

    const cardTitle = isTrial ? `You have ${numDaysRemainingInTrial} days remaining in your trial.` : 'Current Plan';

    return (
        <>
            <Box className="current-plan">
                <Card
                    data-testid="card"
                    classes={{
                        root: 'card'
                    }}
                    elevation={2}
                >
                    <CardHeader
                        title={cardTitle}
                        classes={{
                            root: 'padding-desktop'
                        }}
                        slotProps={{ title: { variant: 'h5' } }}
                    />
                    <Box className="subscription-plan-select">
                        <Box>
                            {subscriptions?.length && (
                                <FormSelectField
                                    disabled={hasYearlyLicense || isLicenseOverdue}
                                    onClose={() => {
                                        setMenuOpen(false);
                                    }}
                                    onOpen={() => setMenuOpen(true)}
                                    open={menuOpen}
                                    required
                                    value={selectedPlan}
                                    renderValue={(value: any) => <ListItemText primary={value.title} />}
                                    onChange={(event: React.ChangeEvent<{ value: SubscriptionOption }>) => {
                                        const option = event.target.value;
                                        if (option.isEnabled) {
                                            setSelectedPlan(option);
                                            setMenuOpen(false);
                                        }
                                    }}
                                >
                                    {generatePlanOptions()}
                                </FormSelectField>
                            )}
                        </Box>
                    </Box>
                    <CardActions
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: isSmallScreen ? '16px' : '32px'
                        }}
                    >
                        <SecondaryButton
                            sx={{ width: '100%', marginBottom: '16px' }}
                            disabled={hasYearlyLicense && !isLicenseOverdue}
                            loading={isUpgradeOrDowngradeButtonLoading}
                            onClick={handleButtonClick}
                        >
                            {generateButtonText()}
                        </SecondaryButton>
                        {selectedPlan !== currentPlan ? (
                            <TertiaryButton onClick={() => cancelChanges()}>Cancel Changes</TertiaryButton>
                        ) : null}
                        {showCancelPlanButton && (
                            <TertiaryButton
                                disabled={isCancelPending || isMarketplaceCoach}
                                onClick={() => {
                                    return history.push({
                                        pathname: '/subscription-plans/cancel',
                                        state: { nextBillingDate }
                                    });
                                }}
                            >
                                Cancel Plan
                            </TertiaryButton>
                        )}
                    </CardActions>
                </Card>
                {isLicenseOverdue && (
                    <HelperTextBox>
                        <Typography sx={{ fontSize: '14px', color: theme.palette.error.main, fontWeight: 500 }}>
                            There is an issue with your payment information. Please go to Payment Info to update your
                            payment&nbsp;method.
                        </Typography>
                    </HelperTextBox>
                )}
                {!isLicenseOverdue && hasYearlyLicense && (
                    <HelperTextBox>
                        <Typography variant="caption">
                            Please contact{' '}
                            <Link
                                href="https://support.trainheroic.com/hc/en-us/requests/new"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Support
                            </Link>{' '}
                            to upgrade your&nbsp;account.
                        </Typography>
                    </HelperTextBox>
                )}
                {isMarketplaceCoach && (
                    <HelperTextBox>
                        <Typography variant="caption" data-testid="marketplace-coach">
                            Your account has active marketplace teams or programs. To cancel, contact&nbsp;
                            <Link
                                href="https://support.trainheroic.com/hc/en-us/requests/new"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Support
                            </Link>
                            .
                        </Typography>
                    </HelperTextBox>
                )}
                {!hasYearlyLicense && (
                    <HelperTextBox>
                        <Typography variant="caption" data-testid="need-more-athletes">
                            Need more than 1000 athletes? Contact&nbsp;
                            <Link
                                href="https://support.trainheroic.com/hc/en-us/requests/new"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Support
                            </Link>
                            .
                        </Typography>
                    </HelperTextBox>
                )}
            </Box>
        </>
    );
}
